import React, { useEffect, useMemo, useState } from "react";
import {
  FiAlertTriangle,
  FiBriefcase,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiCreditCard,
  FiDollarSign,
  FiEdit3,
  FiFlag,
  FiPlus,
  FiRefreshCw,
  FiTrash2,
  FiSearch,
  FiUnlock,
  FiX,
} from "react-icons/fi";
import { getProyectos, createProyecto, deleteProyecto, updateProyecto } from "../../services/api";

const STORAGE_PROJECTS = "ipdepot_proyectos_v5_projects";
const STORAGE_EVENTS = "ipdepot_proyectos_v5_events";
function normalizeApiProjects(responseData) {
  if (Array.isArray(responseData)) return responseData;

  if (Array.isArray(responseData?.items)) return responseData.items;
  if (Array.isArray(responseData?.proyectos)) return responseData.proyectos;
  if (Array.isArray(responseData?.data)) return responseData.data;

  return [];
}


async function refrescarProyectosDesdeApi(setProyectos, setSelectedId) {
  const response = await getProyectos();
  const apiProjects = normalizeApiProjects(response?.data);

  setProyectos(apiProjects);

  if (Array.isArray(apiProjects) && apiProjects.length > 0) {
    setSelectedId((current) => {
      const exists = apiProjects.some((p) => p.id === current);
      return exists ? current : apiProjects[0].id;
    });
  } else {
    setSelectedId(null);
  }

  return apiProjects;
}
const ESTADOS = {
  BIEN: "Va bien",
  ATENCION: "Atención",
  RIESGO: "Riesgo",
  BLOQUEADO: "Bloqueado",
};

const PAGOS = {
  PAGADO: "Pagado",
  PARCIAL: "Parcial",
  PENDIENTE: "Pendiente",
  VENCIDO: "Vencido",
  SIN_MONTO: "Sin monto",
};

const PRIORIDADES = {
  ALTA: "Alta",
  MEDIA: "Media",
  BAJA: "Baja",
};

const ETAPAS = [
  "Idea",
  "Planeación",
  "Análisis",
  "Diseño",
  "Desarrollo",
  "Pruebas",
  "Producción",
  "Soporte",
  "Pausado",
  "Cerrado",
];

const proyectosIniciales = [];

const emptyProject = {
  nombre: "",
  cliente: "IP DEPOT",
  responsable: "",
  etapa: "Idea",
  estado: "BIEN",
  prioridad: "MEDIA",
  avance: 0,
  fechaCompromiso: "",
  proximoPaso: "",
  bloqueo: "",
  decision: "",
  montoContratado: 0,
  montoPagado: 0,
  fechaProximoPago: "",
  notaPago: "",
};

function money(value) {
  return Number(value || 0).toLocaleString("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function fecha(value) {
  if (!value) return "Sin fecha";
  const d = new Date(`${value}T12:00:00`);
  if (Number.isNaN(d.getTime())) return "Sin fecha";

  return d.toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function fechaHora(value) {
  if (!value) return "Sin fecha";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "Sin fecha";

  return d.toLocaleString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function diasRestantes(value) {
  if (!value) return null;
  const hoy = new Date();
  const d = new Date(`${value}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));
}

function normalizar(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function porcentaje(pagado, contratado) {
  const c = Number(contratado || 0);
  const p = Number(pagado || 0);
  if (c <= 0) return 0;
  return Math.min(100, Math.round((p / c) * 100));
}

function infoPago(project) {
  const contratado = Number(project.montoContratado || 0);
  const pagado = Number(project.montoPagado || 0);
  const pendiente = Math.max(contratado - pagado, 0);
  const pct = porcentaje(pagado, contratado);
  const diasPago = diasRestantes(project.fechaProximoPago);

  let estado = "SIN_MONTO";

  if (contratado <= 0) estado = "SIN_MONTO";
  else if (pendiente <= 0) estado = "PAGADO";
  else if (diasPago !== null && diasPago < 0) estado = "VENCIDO";
  else if (pagado > 0) estado = "PARCIAL";
  else estado = "PENDIENTE";

  return { contratado, pagado, pendiente, pct, estado, diasPago };
}

function estadoClass(estado) {
  if (estado === "BIEN") return "ok";
  if (estado === "ATENCION") return "warn";
  if (estado === "RIESGO") return "risk";
  return "blocked";
}

function pagoClass(estado) {
  if (estado === "PAGADO") return "ok";
  if (estado === "PARCIAL") return "info";
  if (estado === "PENDIENTE") return "warn";
  if (estado === "VENCIDO") return "risk";
  return "neutral";
}

function prioridadScore(project) {
  const pago = infoPago(project);
  let score = 0;

  if (project.estado === "ATENCION") score += 25;
  if (project.estado === "RIESGO") score += 45;
  if (project.estado === "BLOQUEADO") score += 60;
  if (project.prioridad === "ALTA") score += 15;
  if (project.bloqueo) score += 25;
  if (project.decision) score += 10;
  if (pago.estado === "VENCIDO") score += 30;
  if (pago.estado === "PENDIENTE") score += 20;

  return Math.min(score, 100);
}

function buildInitialEvents(projects) {
  const now = new Date().toISOString();

  return projects.map((p, index) => ({
    id: `EVT-DEMO-${index + 1}`,
    proyectoId: p.id,
    tipo: "PROYECTO_CREADO",
    titulo: "Proyecto creado",
    descripcion: `Se registró el proyecto ${p.nombre}.`,
    fecha: now,
    usuario: "Dirección de Proyectos",
    datos: {
      estado: p.estado,
      avance: p.avance,
      montoContratado: p.montoContratado,
    },
  }));
}

function newEvent({ proyectoId, tipo, titulo, descripcion, datos = {} }) {
  return {
    id: `EVT-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    proyectoId,
    tipo,
    titulo,
    descripcion,
    fecha: new Date().toISOString(),
    usuario: "Dirección de Proyectos",
    datos,
  };
}

export default function ProyectosDashboardView() {
  const [proyectos, setProyectos] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_PROJECTS);
      if (!saved) return proyectosIniciales;
      const parsed = JSON.parse(saved);
      return Array.isArray(parsed) && parsed.length ? parsed : proyectosIniciales;
    } catch {
      return proyectosIniciales;
    }
  });

  const [eventos, setEventos] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_EVENTS);
      if (!saved) return buildInitialEvents(proyectosIniciales);
      const parsed = JSON.parse(saved);
      return Array.isArray(parsed) ? parsed : buildInitialEvents(proyectosIniciales);
    } catch {
      return buildInitialEvents(proyectosIniciales);
    }
  });

  const [busqueda, setBusqueda] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("TODOS");
  const [filtroPago, setFiltroPago] = useState("TODOS");
  const [selectedId, setSelectedId] = useState(proyectos[0]?.id || null);

  const [projectModal, setProjectModal] = useState(false);
  const [projectForm, setProjectForm] = useState(emptyProject);

  const [actionModal, setActionModal] = useState(null);
  const [actionForm, setActionForm] = useState({});

  useEffect(() => {
    let alive = true;

    async function cargarProyectosDesdeApi() {
      try {
        const response = await getProyectos();
        const apiProjects = normalizeApiProjects(response?.data);

        if (!alive) return;

        if (Array.isArray(apiProjects)) {
          setProyectos(apiProjects);
        }
      } catch (error) {
        console.warn("No se pudieron cargar proyectos desde API. Se usa cache local.", error);
      }
    }

    cargarProyectosDesdeApi();

    return () => {
      alive = false;
    };
  }, []);
  useEffect(() => {
    localStorage.setItem(STORAGE_PROJECTS, JSON.stringify(proyectos));
  }, [proyectos]);

  useEffect(() => {
    localStorage.setItem(STORAGE_EVENTS, JSON.stringify(eventos));
  }, [eventos]);

  const data = useMemo(() => {
    return proyectos.map((p) => ({
      ...p,
      pago: infoPago(p),
      score: prioridadScore(p),
    }));
  }, [proyectos]);

  const filtrados = useMemo(() => {
    const q = normalizar(busqueda);

    return data.filter((p) => {
      const texto = normalizar(
        `${p.nombre} ${p.cliente} ${p.responsable} ${p.estado} ${p.etapa}`
      );

      const okTexto = texto.includes(q);
      const okEstado = filtroEstado === "TODOS" || p.estado === filtroEstado;
      const okPago = filtroPago === "TODOS" || p.pago.estado === filtroPago;

      return okTexto && okEstado && okPago;
    });
  }, [data, busqueda, filtroEstado, filtroPago]);

  const selected = data.find((p) => p.id === selectedId) || filtrados[0] || data[0];

  const eventosProyecto = useMemo(() => {
    if (!selected) return [];

    return eventos
      .filter((e) => e.proyectoId === selected.id)
      .sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
  }, [eventos, selected]);

  const resumen = useMemo(() => {
    const contratado = data.reduce((a, p) => a + p.pago.contratado, 0);
    const cobrado = data.reduce((a, p) => a + p.pago.pagado, 0);
    const pendiente = data.reduce((a, p) => a + p.pago.pendiente, 0);

    return {
      activos: data.length,
      bien: data.filter((p) => p.estado === "BIEN").length,
      atencion: data.filter(
        (p) =>
          p.estado !== "BIEN" ||
          p.bloqueo ||
          p.decision ||
          p.pago.estado === "VENCIDO"
      ).length,
      bloqueados: data.filter((p) => p.estado === "BLOQUEADO" || p.bloqueo).length,
      contratado,
      cobrado,
      pendiente,
      cobranza: contratado > 0 ? Math.round((cobrado / contratado) * 100) : 0,
      vencidos: data.filter((p) => p.pago.estado === "VENCIDO").length,
    };
  }, [data]);

  const compromisos = useMemo(() => {
    const entregas = data.map((p) => ({
      id: `${p.id}-entrega`,
      projectId: p.id,
      tipo: "Entrega",
      nombre: p.nombre,
      responsable: p.responsable,
      fecha: p.fechaCompromiso,
    }));

    const pagos = data
      .filter((p) => p.fechaProximoPago && p.pago.pendiente > 0)
      .map((p) => ({
        id: `${p.id}-pago`,
        projectId: p.id,
        tipo: "Pago",
        nombre: p.nombre,
        responsable: p.responsable,
        fecha: p.fechaProximoPago,
      }));

    return [...entregas, ...pagos]
      .filter((x) => x.fecha)
      .sort((a, b) => new Date(a.fecha) - new Date(b.fecha))
      .slice(0, 6);
  }, [data]);

  function addEvent(event) {
    setEventos((prev) => [event, ...prev]);
  }

  function updateProject(projectId, updater) {
    setProyectos((prev) =>
      prev.map((project) => {
        if (project.id !== projectId) return project;
        return updater(project);
      })
    );
  }

  function restaurar() {
    localStorage.removeItem(STORAGE_PROJECTS);
    localStorage.removeItem(STORAGE_EVENTS);

    refrescarProyectosDesdeApi(setProyectos, setSelectedId)
      .then((apiProjects) => {
        setEventos(buildInitialEvents(apiProjects || []));
      })
      .catch((error) => {
        console.error("No se pudieron restaurar proyectos desde API.", error);
        setProyectos([]);
        setEventos([]);
        setSelectedId(null);
      });
  }


      async function eliminarProyectoSeleccionado() {
    const proyectoActual = proyectos.find((p) => p.id === selectedId) || null;

    if (!proyectoActual?.id) {
      alert("No hay proyecto seleccionado para eliminar.");
      return;
    }

    const ok = window.confirm(
      `Vas a eliminar el proyecto "${proyectoActual.nombre}". Esta accion eliminara el registro en DynamoDB. Deseas continuar?`
    );

    if (!ok) return;

    try {
      await deleteProyecto(proyectoActual.id);

      localStorage.removeItem(STORAGE_PROJECTS);
      localStorage.removeItem(STORAGE_EVENTS);

      await refrescarProyectosDesdeApi(setProyectos, setSelectedId);

      setEventos((prev) =>
        Array.isArray(prev)
          ? prev.filter((evento) => evento.proyectoId !== proyectoActual.id)
          : []
      );

      console.log("Proyecto eliminado correctamente:", proyectoActual.id);
    } catch (error) {
      console.error("No se pudo eliminar el proyecto en DynamoDB.", error);
      alert("No se pudo eliminar el proyecto en DynamoDB. Revisa la consola.");
    }
  }
  function crearProyecto(e) {
    e.preventDefault();

    const nuevo = {
      ...projectForm,
      id: `PRY-${String(Date.now()).slice(-5)}`,
      avance: Math.min(100, Math.max(0, Number(projectForm.avance || 0))),
      montoContratado: Number(projectForm.montoContratado || 0),
      montoPagado: Number(projectForm.montoPagado || 0),
    };

    createProyecto(nuevo)
      .then(async () => {
        await refrescarProyectosDesdeApi(setProyectos, setSelectedId);
      })
      .catch((error) => {
        console.error("No se pudo guardar el proyecto en DynamoDB.", error);
        alert("No se pudo guardar el proyecto en DynamoDB. No se agregara a la pantalla.");
      });
    setEventos((prev) => [
      newEvent({
        proyectoId: nuevo.id,
        tipo: "PROYECTO_CREADO",
        titulo: "Proyecto creado",
        descripcion: `Se registró el proyecto ${nuevo.nombre}.`,
        datos: {
          estado: nuevo.estado,
          avance: nuevo.avance,
          montoContratado: nuevo.montoContratado,
        },
      }),
      ...prev,
    ]);

    setSelectedId(nuevo.id);
    setProjectForm(emptyProject);
    setProjectModal(false);
  }

  function abrirAccion(tipo) {
    if (!selected) return;

    const defaults = {
      AVANCE: {
        avance: selected.avance,
        proximoPaso: selected.proximoPaso || "",
        comentario: "",
      },
      PAGO: {
        monto: "",
        fechaPago: new Date().toISOString().slice(0, 10),
        fechaProximoPago: selected.fechaProximoPago || "",
        notaPago: "",
      },
      ESTADO: {
        estado: selected.estado,
        etapa: selected.etapa,
        comentario: "",
      },
      BLOQUEO: {
        bloqueo: selected.bloqueo || "",
        comentario: "",
      },
      RESOLVER_BLOQUEO: {
        comentario: "",
      },
      DECISION: {
        decision: selected.decision || "",
        comentario: "",
      },
      FECHA: {
        fechaCompromiso: selected.fechaCompromiso || "",
        comentario: "",
      },
      NOTA: {
        nota: "",
      },
    };

    setActionForm(defaults[tipo] || {});
    setActionModal(tipo);
  }

  function guardarAccion(e) {
    e.preventDefault();
    if (!selected || !actionModal) return;

    const projectId = selected.id;

    if (actionModal === "AVANCE") {
      const avanceAnterior = Number(selected.avance || 0);
      const avanceNuevo = Math.min(100, Math.max(0, Number(actionForm.avance || 0)));
      const proximoPaso = actionForm.proximoPaso || selected.proximoPaso || "";

      updateProject(projectId, (p) => ({
        ...p,
        avance: avanceNuevo,
        proximoPaso,
      }));

      addEvent(
        newEvent({
          proyectoId: projectId,
          tipo: "AVANCE_ACTUALIZADO",
          titulo: "Avance actualizado",
          descripcion: `Avance de ${avanceAnterior}% a ${avanceNuevo}%.`,
          datos: {
            avanceAnterior,
            avanceNuevo,
            proximoPaso,
            comentario: actionForm.comentario || "",
          },
        })
      );
    }

    if (actionModal === "PAGO") {
      const monto = Number(actionForm.monto || 0);
      const pagadoAnterior = Number(selected.montoPagado || 0);
      const pagadoNuevo = pagadoAnterior + monto;

      updateProject(projectId, (p) => ({
        ...p,
        montoPagado: pagadoNuevo,
        fechaProximoPago: actionForm.fechaProximoPago || p.fechaProximoPago,
        notaPago: actionForm.notaPago || p.notaPago,
      }));

      addEvent(
        newEvent({
          proyectoId: projectId,
          tipo: "PAGO_REGISTRADO",
          titulo: "Pago registrado",
          descripcion: `Se registró pago por ${money(monto)}.`,
          datos: {
            monto,
            fechaPago: actionForm.fechaPago,
            pagadoAnterior,
            pagadoNuevo,
            notaPago: actionForm.notaPago || "",
          },
        })
      );
    }

    if (actionModal === "ESTADO") {
      const estadoAnterior = selected.estado;
      const etapaAnterior = selected.etapa;
      const estadoNuevo = actionForm.estado;
      const etapaNueva = actionForm.etapa;

      updateProject(projectId, (p) => ({
        ...p,
        estado: estadoNuevo,
        etapa: etapaNueva,
      }));

      addEvent(
        newEvent({
          proyectoId: projectId,
          tipo: "ESTADO_CAMBIADO",
          titulo: "Estado actualizado",
          descripcion: `Estado de ${ESTADOS[estadoAnterior]} a ${ESTADOS[estadoNuevo]}.`,
          datos: {
            estadoAnterior,
            estadoNuevo,
            etapaAnterior,
            etapaNueva,
            comentario: actionForm.comentario || "",
          },
        })
      );
    }

    if (actionModal === "BLOQUEO") {
      const bloqueoAnterior = selected.bloqueo || "";
      const bloqueoNuevo = actionForm.bloqueo || "";

      updateProject(projectId, (p) => ({
        ...p,
        bloqueo: bloqueoNuevo,
        estado: bloqueoNuevo ? "BLOQUEADO" : p.estado,
      }));

      addEvent(
        newEvent({
          proyectoId: projectId,
          tipo: "BLOQUEO_AGREGADO",
          titulo: "Bloqueo agregado",
          descripcion: bloqueoNuevo || "Se actualizó el bloqueo del proyecto.",
          datos: {
            bloqueoAnterior,
            bloqueoNuevo,
            comentario: actionForm.comentario || "",
          },
        })
      );
    }

    if (actionModal === "RESOLVER_BLOQUEO") {
      const bloqueoResuelto = selected.bloqueo || "Bloqueo no especificado";

      updateProject(projectId, (p) => ({
        ...p,
        bloqueo: "",
        estado: p.estado === "BLOQUEADO" ? "ATENCION" : p.estado,
      }));

      addEvent(
        newEvent({
          proyectoId: projectId,
          tipo: "BLOQUEO_RESUELTO",
          titulo: "Bloqueo resuelto",
          descripcion: bloqueoResuelto,
          datos: {
            comentario: actionForm.comentario || "",
          },
        })
      );
    }

    if (actionModal === "DECISION") {
      const decisionAnterior = selected.decision || "";
      const decisionNueva = actionForm.decision || "";

      updateProject(projectId, (p) => ({
        ...p,
        decision: decisionNueva,
      }));

      addEvent(
        newEvent({
          proyectoId: projectId,
          tipo: "DECISION_ACTUALIZADA",
          titulo: "Decisión actualizada",
          descripcion: decisionNueva || "Se actualizó la decisión requerida.",
          datos: {
            decisionAnterior,
            decisionNueva,
            comentario: actionForm.comentario || "",
          },
        })
      );
    }

    if (actionModal === "FECHA") {
      const fechaAnterior = selected.fechaCompromiso || "";
      const fechaNueva = actionForm.fechaCompromiso || "";

      updateProject(projectId, (p) => ({
        ...p,
        fechaCompromiso: fechaNueva,
      }));

      addEvent(
        newEvent({
          proyectoId: projectId,
          tipo: "FECHA_CAMBIADA",
          titulo: "Fecha compromiso actualizada",
          descripcion: `Fecha de ${fecha(fechaAnterior)} a ${fecha(fechaNueva)}.`,
          datos: {
            fechaAnterior,
            fechaNueva,
            comentario: actionForm.comentario || "",
          },
        })
      );
    }

    if (actionModal === "NOTA") {
      addEvent(
        newEvent({
          proyectoId: projectId,
          tipo: "NOTA_AGREGADA",
          titulo: "Nota agregada",
          descripcion: actionForm.nota || "Nota sin descripción.",
          datos: {},
        })
      );
    }

    setActionModal(null);
    setActionForm({});
  }

  return (
    <div className="projectPage">
      <style>{`
        .projectPage {
          min-height: 100vh;
          background: #f4f6f9;
          color: #111827;
          padding: 22px;
          font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        }

        .shell {
          max-width: 1560px;
          margin: 0 auto;
        }

        .topbar {
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 18px;
          padding: 18px 20px;
          display: flex;
          justify-content: space-between;
          gap: 18px;
          align-items: center;
          margin-bottom: 12px;
          box-shadow: 0 8px 22px rgba(15, 23, 42, .035);
        }

        .moduleLabel {
          color: #64748b;
          font-size: 11px;
          font-weight: 900;
          letter-spacing: .12em;
          text-transform: uppercase;
          margin-bottom: 5px;
        }

        .title {
          margin: 0;
          font-size: 28px;
          font-weight: 950;
          letter-spacing: -.035em;
        }

        .subtitle {
          margin: 5px 0 0;
          color: #64748b;
          font-size: 13px;
          font-weight: 700;
        }

        .topActions {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .btnPrimary,
        .btnSecondary,
        .btnAction {
          height: 38px;
          border-radius: 10px;
          border: 1px solid transparent;
          padding: 0 13px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          font-size: 13px;
          font-weight: 850;
          cursor: pointer;
        }

        .btnPrimary {
          background: #111827;
          color: #ffffff;
        }

        .btnSecondary {
          background: #ffffff;
          color: #334155;
          border-color: #d8dee8;
        }

        .btnAction {
          background: #f8fafc;
          color: #334155;
          border-color: #d8dee8;
        }

        .btnAction:hover,
        .btnSecondary:hover {
          border-color: #94a3b8;
          background: #f1f5f9;
        }

        .summaryBar {
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          display: grid;
          grid-template-columns: repeat(6, minmax(0, 1fr));
          margin-bottom: 12px;
          overflow: hidden;
        }

        .summaryItem {
          padding: 14px 16px;
          border-right: 1px solid #edf0f4;
        }

        .summaryItem:last-child {
          border-right: 0;
        }

        .summaryLabel {
          color: #64748b;
          font-size: 11px;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: .05em;
          margin-bottom: 6px;
        }

        .summaryValue {
          color: #111827;
          font-size: 22px;
          font-weight: 950;
          letter-spacing: -.03em;
        }

        .filters {
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          padding: 12px;
          display: grid;
          grid-template-columns: minmax(260px, 1fr) 190px 190px;
          gap: 10px;
          margin-bottom: 12px;
        }

        .search,
        .select {
          height: 38px;
          border-radius: 10px;
          border: 1px solid #d8dee8;
          background: #ffffff;
          color: #334155;
          font-weight: 780;
          outline: none;
        }

        .search {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 0 11px;
        }

        .search input {
          border: 0;
          outline: none;
          background: transparent;
          width: 100%;
          font-weight: 780;
          color: #111827;
        }

        .select {
          padding: 0 10px;
        }

        .layout {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 430px;
          gap: 12px;
          align-items: start;
        }

        .panel {
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 18px;
          box-shadow: 0 8px 22px rgba(15, 23, 42, .035);
          overflow: hidden;
        }

        .panelHeader {
          height: 54px;
          padding: 0 16px;
          border-bottom: 1px solid #edf0f4;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }

        .panelTitle {
          margin: 0;
          font-size: 14px;
          font-weight: 950;
          display: flex;
          align-items: center;
          gap: 8px;
          color: #111827;
        }

        .muted {
          color: #94a3b8;
          font-size: 12px;
          font-weight: 850;
        }

        .tableWrap {
          overflow-x: auto;
        }

        .projectTable {
          width: 100%;
          min-width: 1080px;
          border-collapse: collapse;
        }

        .projectTable th {
          text-align: left;
          background: #f8fafc;
          color: #64748b;
          font-size: 11px;
          font-weight: 950;
          text-transform: uppercase;
          letter-spacing: .055em;
          padding: 12px 14px;
          border-bottom: 1px solid #e5e7eb;
        }

        .projectTable td {
          padding: 13px 14px;
          border-bottom: 1px solid #edf0f4;
          color: #334155;
          font-size: 13px;
          font-weight: 720;
          vertical-align: middle;
        }

        .projectTable tr {
          cursor: pointer;
        }

        .projectTable tr:hover td {
          background: #f8fafc;
        }

        .projectTable tr.active td {
          background: #f1f5f9;
        }

        .projectName {
          font-size: 13px;
          font-weight: 950;
          color: #111827;
          margin-bottom: 3px;
        }

        .projectMeta {
          color: #64748b;
          font-size: 12px;
          font-weight: 720;
        }

        .badge {
          display: inline-flex;
          align-items: center;
          border-radius: 999px;
          padding: 5px 9px;
          font-size: 11px;
          font-weight: 950;
          white-space: nowrap;
        }

        .ok {
          background: #ecfdf3;
          color: #166534;
        }

        .warn {
          background: #fffbeb;
          color: #92400e;
        }

        .risk {
          background: #fef2f2;
          color: #991b1b;
        }

        .blocked {
          background: #f3f0ff;
          color: #5b21b6;
        }

        .info {
          background: #eff6ff;
          color: #1d4ed8;
        }

        .neutral {
          background: #f1f5f9;
          color: #475569;
        }

        .progressMini {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .track {
          width: 92px;
          height: 7px;
          background: #e5e7eb;
          border-radius: 999px;
          overflow: hidden;
        }

        .fill {
          height: 100%;
          background: #111827;
          border-radius: 999px;
        }

        .fillMoney {
          height: 100%;
          background: #16a34a;
          border-radius: 999px;
        }

        .sidePanel {
          position: sticky;
          top: 16px;
        }

        .sideBody {
          padding: 16px;
        }

        .selectedTitle {
          font-size: 20px;
          font-weight: 950;
          letter-spacing: -.03em;
          margin: 0 0 10px;
          color: #111827;
        }

        .badgeRow {
          display: flex;
          gap: 7px;
          flex-wrap: wrap;
          margin-bottom: 14px;
        }

        .detailGrid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 9px;
          margin: 14px 0;
        }

        .detailBox {
          background: #f8fafc;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 10px;
        }

        .label {
          color: #64748b;
          font-size: 10px;
          font-weight: 950;
          text-transform: uppercase;
          letter-spacing: .055em;
          margin-bottom: 5px;
        }

        .value {
          color: #111827;
          font-size: 13px;
          font-weight: 880;
          line-height: 1.35;
        }

        .progressBlock {
          margin: 12px 0;
        }

        .progressTop {
          display: flex;
          justify-content: space-between;
          color: #334155;
          font-size: 12px;
          font-weight: 900;
          margin-bottom: 7px;
        }

        .trackFull {
          height: 9px;
          background: #e5e7eb;
          border-radius: 999px;
          overflow: hidden;
        }

        .actionBox {
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 13px;
          padding: 11px;
          margin-top: 9px;
        }

        .actionTitle {
          color: #111827;
          font-size: 12px;
          font-weight: 950;
          margin-bottom: 5px;
        }

        .actionText {
          color: #475569;
          font-size: 13px;
          font-weight: 720;
          line-height: 1.38;
        }

        .quickActions {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 8px;
          margin: 14px 0;
        }

        .dangerZone {
          margin: 12px 0 4px;
          padding-top: 12px;
          border-top: 1px solid #edf0f4;
        }

        .btnDanger {
          width: 100%;
          height: 38px;
          border-radius: 10px;
          border: 1px solid #fecaca;
          background: #fff1f2;
          color: #991b1b;
          padding: 0 13px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          font-size: 13px;
          font-weight: 900;
          cursor: pointer;
        }

        .btnDanger:hover {
          background: #fee2e2;
          border-color: #fca5a5;
        }

        .timeline {
          border-top: 1px solid #edf0f4;
          padding: 14px 16px 16px;
          max-height: 360px;
          overflow: auto;
        }

        .timelineTitle {
          font-size: 13px;
          font-weight: 950;
          margin: 0 0 10px;
          color: #111827;
        }

        .event {
          display: grid;
          grid-template-columns: 78px 1fr;
          gap: 10px;
          padding: 9px 0;
          border-bottom: 1px solid #f1f5f9;
        }

        .event:last-child {
          border-bottom: 0;
        }

        .eventDate {
          color: #94a3b8;
          font-size: 11px;
          font-weight: 850;
          line-height: 1.35;
        }

        .eventTitle {
          color: #111827;
          font-size: 13px;
          font-weight: 950;
          margin-bottom: 3px;
        }

        .eventText {
          color: #475569;
          font-size: 12px;
          font-weight: 720;
          line-height: 1.38;
        }

        .commitments {
          margin-top: 12px;
          display: grid;
          grid-template-columns: minmax(0, 1fr) 430px;
          gap: 12px;
        }

        .commitmentList {
          padding: 10px;
          display: grid;
          gap: 8px;
        }

        .commitment {
          border: 1px solid #e5e7eb;
          background: #ffffff;
          border-radius: 12px;
          padding: 10px;
          cursor: pointer;
          text-align: left;
        }

        .commitment:hover {
          background: #f8fafc;
        }

        .rowBetween {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 10px;
        }

        .modalOverlay {
          position: fixed;
          inset: 0;
          z-index: 1000;
          background: rgba(15, 23, 42, .46);
          display: grid;
          place-items: center;
          padding: 18px;
          backdrop-filter: blur(5px);
        }

        .modal {
          width: min(820px, 100%);
          max-height: 92vh;
          overflow: auto;
          background: #ffffff;
          border-radius: 18px;
          border: 1px solid #e5e7eb;
          box-shadow: 0 24px 60px rgba(15, 23, 42, .24);
        }

        .modalSmall {
          width: min(620px, 100%);
        }

        .modalHeader {
          height: 58px;
          padding: 0 18px;
          border-bottom: 1px solid #edf0f4;
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: sticky;
          top: 0;
          background: #ffffff;
          z-index: 2;
        }

        .modalTitle {
          margin: 0;
          font-size: 18px;
          font-weight: 950;
          color: #111827;
        }

        .close {
          width: 34px;
          height: 34px;
          border-radius: 10px;
          border: 1px solid #d8dee8;
          background: #ffffff;
          color: #334155;
          cursor: pointer;
        }

        .modalBody {
          padding: 16px 18px;
        }

        .formGrid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
        }

        .field.full {
          grid-column: 1 / -1;
        }

        .field label {
          display: block;
          color: #334155;
          font-size: 12px;
          font-weight: 950;
          margin-bottom: 6px;
        }

        .field input,
        .field select,
        .field textarea {
          width: 100%;
          border: 1px solid #d8dee8;
          background: #ffffff;
          border-radius: 10px;
          padding: 10px 11px;
          outline: none;
          font-weight: 760;
          color: #111827;
        }

        .field textarea {
          min-height: 72px;
          resize: vertical;
        }

        .modalActions {
          padding: 0 18px 18px;
          display: flex;
          justify-content: flex-end;
          gap: 8px;
        }

        @media (max-width: 1280px) {
          .layout,
          .commitments {
            grid-template-columns: 1fr;
          }

          .sidePanel {
            position: static;
          }

          .summaryBar {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }

          .summaryItem:nth-child(3) {
            border-right: 0;
          }
        }

        @media (max-width: 760px) {
          .projectPage {
            padding: 14px;
          }

          .topbar {
            align-items: flex-start;
            flex-direction: column;
          }

          .summaryBar,
          .filters,
          .detailGrid,
          .quickActions,
          .formGrid {
            grid-template-columns: 1fr;
          }

          .summaryItem {
            border-right: 0;
            border-bottom: 1px solid #edf0f4;
          }

          .summaryItem:last-child {
            border-bottom: 0;
          }

          .title {
            font-size: 24px;
          }
        }
      `}</style>

      <div className="shell">
        <section className="topbar">
          <div>
            <div className="moduleLabel">IP DEPOT · Dirección</div>
            <h1 className="title">Centro de Proyectos</h1>
            <p className="subtitle">Control operativo, pagos y bitácora de evolución.</p>
          </div>

          <div className="topActions">
            <button type="button" className="btnSecondary" onClick={restaurar}>
              <FiRefreshCw /> Restaurar
            </button>

            <button type="button" className="btnPrimary" onClick={() => setProjectModal(true)}>
              <FiPlus /> Nuevo proyecto
            </button>
          </div>
        </section>

        <section className="summaryBar">
          <div className="summaryItem">
            <div className="summaryLabel">Activos</div>
            <div className="summaryValue">{resumen.activos}</div>
          </div>

          <div className="summaryItem">
            <div className="summaryLabel">Atención</div>
            <div className="summaryValue">{resumen.atencion}</div>
          </div>

          <div className="summaryItem">
            <div className="summaryLabel">Bloqueados</div>
            <div className="summaryValue">{resumen.bloqueados}</div>
          </div>

          <div className="summaryItem">
            <div className="summaryLabel">Cobrado</div>
            <div className="summaryValue">{resumen.cobranza}%</div>
          </div>

          <div className="summaryItem">
            <div className="summaryLabel">Por cobrar</div>
            <div className="summaryValue">{money(resumen.pendiente)}</div>
          </div>

          <div className="summaryItem">
            <div className="summaryLabel">Vencidos</div>
            <div className="summaryValue">{resumen.vencidos}</div>
          </div>
        </section>

        <section className="filters">
          <div className="search">
            <FiSearch color="#64748b" />
            <input
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar proyecto, responsable o cliente..."
            />
          </div>

          <select
            className="select"
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
          >
            <option value="TODOS">Todos los estados</option>
            <option value="BIEN">Va bien</option>
            <option value="ATENCION">Atención</option>
            <option value="RIESGO">Riesgo</option>
            <option value="BLOQUEADO">Bloqueado</option>
          </select>

          <select
            className="select"
            value={filtroPago}
            onChange={(e) => setFiltroPago(e.target.value)}
          >
            <option value="TODOS">Todos los pagos</option>
            <option value="PAGADO">Pagado</option>
            <option value="PARCIAL">Parcial</option>
            <option value="PENDIENTE">Pendiente</option>
            <option value="VENCIDO">Vencido</option>
            <option value="SIN_MONTO">Sin monto</option>
          </select>
        </section>

        <section className="layout">
          <div className="panel">
            <div className="panelHeader">
              <h2 className="panelTitle">
                <FiBriefcase /> Portafolio
              </h2>
              <span className="muted">{filtrados.length} proyectos</span>
            </div>

            <div className="tableWrap">
              <table className="projectTable">
                <thead>
                  <tr>
                    <th>Proyecto</th>
                    <th>Estado</th>
                    <th>Pago</th>
                    <th>Avance</th>
                    <th>Pagado</th>
                    <th>Responsable</th>
                    <th>Próximo paso</th>
                    <th>Por cobrar</th>
                  </tr>
                </thead>

                <tbody>
                  {filtrados.map((p) => (
                    <tr
                      key={p.id}
                      className={selected?.id === p.id ? "active" : ""}
                      onClick={() => setSelectedId(p.id)}
                    >
                      <td>
                        <div className="projectName">{p.nombre}</div>
                        <div className="projectMeta">
                          {p.id} · {p.cliente} · {p.etapa}
                        </div>
                      </td>

                      <td>
                        <span className={`badge ${estadoClass(p.estado)}`}>
                          {ESTADOS[p.estado]}
                        </span>
                      </td>

                      <td>
                        <span className={`badge ${pagoClass(p.pago.estado)}`}>
                          {PAGOS[p.pago.estado]}
                        </span>
                      </td>

                      <td>
                        <div className="progressMini">
                          <div className="track">
                            <div className="fill" style={{ width: `${p.avance}%` }} />
                          </div>
                          <strong>{p.avance}%</strong>
                        </div>
                      </td>

                      <td>
                        <div className="progressMini">
                          <div className="track">
                            <div className="fillMoney" style={{ width: `${p.pago.pct}%` }} />
                          </div>
                          <strong>{p.pago.pct}%</strong>
                        </div>
                      </td>

                      <td>{p.responsable}</td>
                      <td>{p.proximoPaso || "Sin acción"}</td>
                      <td><strong>{money(p.pago.pendiente)}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <aside className="panel sidePanel">
            <div className="panelHeader">
              <h2 className="panelTitle">
                <FiEdit3 /> Ficha
              </h2>
              <span className="muted">{selected?.id}</span>
            </div>

            {selected && (
              <>
                <div className="sideBody">
                  <h2 className="selectedTitle">{selected.nombre}</h2>

                  <div className="badgeRow">
                    <span className={`badge ${estadoClass(selected.estado)}`}>
                      {ESTADOS[selected.estado]}
                    </span>
                    <span className={`badge ${pagoClass(selected.pago.estado)}`}>
                      {PAGOS[selected.pago.estado]}
                    </span>
                    <span className="badge neutral">
                      Prioridad {PRIORIDADES[selected.prioridad]}
                    </span>
                  </div>

                  <div className="progressBlock">
                    <div className="progressTop">
                      <span>Avance</span>
                      <span>{selected.avance}%</span>
                    </div>
                    <div className="trackFull">
                      <div className="fill" style={{ width: `${selected.avance}%` }} />
                    </div>
                  </div>

                  <div className="progressBlock">
                    <div className="progressTop">
                      <span>Pagado</span>
                      <span>{selected.pago.pct}%</span>
                    </div>
                    <div className="trackFull">
                      <div className="fillMoney" style={{ width: `${selected.pago.pct}%` }} />
                    </div>
                  </div>

                  <div className="detailGrid">
                    <div className="detailBox">
                      <div className="label">Responsable</div>
                      <div className="value">{selected.responsable}</div>
                    </div>

                    <div className="detailBox">
                      <div className="label">Entrega</div>
                      <div className="value">{fecha(selected.fechaCompromiso)}</div>
                    </div>

                    <div className="detailBox">
                      <div className="label">Cobrado</div>
                      <div className="value">{money(selected.pago.pagado)}</div>
                    </div>

                    <div className="detailBox">
                      <div className="label">Pendiente</div>
                      <div className="value">{money(selected.pago.pendiente)}</div>
                    </div>
                  </div>

                  <div className="quickActions">
                    <button className="btnAction" type="button" onClick={() => abrirAccion("AVANCE")}>
                      <FiCheckCircle /> Avance
                    </button>
                    <button className="btnAction" type="button" onClick={() => abrirAccion("PAGO")}>
                      <FiDollarSign /> Pago
                    </button>
                    <button className="btnAction" type="button" onClick={() => abrirAccion("ESTADO")}>
                      <FiFlag /> Estado
                    </button>
                    <button className="btnAction" type="button" onClick={() => abrirAccion("BLOQUEO")}>
                      <FiAlertTriangle /> Bloqueo
                    </button>
                    <button className="btnAction" type="button" onClick={() => abrirAccion("RESOLVER_BLOQUEO")}>
                      <FiUnlock /> Resolver
                    </button>
                    <button className="btnAction" type="button" onClick={() => abrirAccion("DECISION")}>
                      <FiEdit3 /> Decisión
                    </button>
                    <button className="btnAction" type="button" onClick={() => abrirAccion("FECHA")}>
                      <FiCalendar /> Fecha
                    </button>
                    <button className="btnAction" type="button" onClick={() => abrirAccion("NOTA")}>
                      <FiClock /> Nota
                    </button>
                  </div>
                  <div className="dangerZone">
                    <button
                      className="btnDanger"
                      type="button"
                      onClick={eliminarProyectoSeleccionado}
                    >
                      <FiTrash2 /> Eliminar proyecto
                    </button>
                  </div>


                  <div className="actionBox">
                    <div className="actionTitle">Siguiente acción</div>
                    <div className="actionText">{selected.proximoPaso || "Sin acción registrada."}</div>
                  </div>

                  <div className="actionBox">
                    <div className="actionTitle">Bloqueo</div>
                    <div className="actionText">{selected.bloqueo || "Sin bloqueo."}</div>
                  </div>

                  <div className="actionBox">
                    <div className="actionTitle">Decisión</div>
                    <div className="actionText">{selected.decision || "Sin decisión pendiente."}</div>
                  </div>
                </div>

                <div className="timeline">
                  <h3 className="timelineTitle">Línea de vida</h3>

                  {eventosProyecto.length === 0 ? (
                    <div className="eventText">Sin eventos registrados.</div>
                  ) : (
                    eventosProyecto.map((event) => (
                      <div className="event" key={event.id}>
                        <div className="eventDate">{fechaHora(event.fecha)}</div>
                        <div>
                          <div className="eventTitle">{event.titulo}</div>
                          <div className="eventText">{event.descripcion}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </aside>
        </section>

        <section className="commitments">
          <div className="panel">
            <div className="panelHeader">
              <h2 className="panelTitle">
                <FiCalendar /> Próximos compromisos
              </h2>
              <span className="muted">Entregas y pagos</span>
            </div>

            <div className="commitmentList">
              {compromisos.map((c) => {
                const dias = diasRestantes(c.fecha);
                const vencido = dias !== null && dias < 0;

                return (
                  <button
                    key={c.id}
                    type="button"
                    className="commitment"
                    onClick={() => setSelectedId(c.projectId)}
                  >
                    <div className="rowBetween">
                      <div>
                        <div className="projectName">{c.nombre}</div>
                        <div className="projectMeta">
                          {c.tipo} · {c.responsable} · {fecha(c.fecha)}
                        </div>
                      </div>

                      <span className={`badge ${vencido ? "risk" : "ok"}`}>
                        {dias === null
                          ? "Sin fecha"
                          : dias < 0
                          ? `${Math.abs(dias)} días vencido`
                          : `${dias} días`}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="panel">
            <div className="panelHeader">
              <h2 className="panelTitle">
                <FiDollarSign /> Finanzas
              </h2>
              <span className="muted">Portafolio</span>
            </div>

            <div className="detailGrid" style={{ padding: 12, margin: 0 }}>
              <div className="detailBox">
                <div className="label">Contratado</div>
                <div className="value">{money(resumen.contratado)}</div>
              </div>

              <div className="detailBox">
                <div className="label">Cobrado</div>
                <div className="value">{money(resumen.cobrado)}</div>
              </div>

              <div className="detailBox">
                <div className="label">Pendiente</div>
                <div className="value">{money(resumen.pendiente)}</div>
              </div>

              <div className="detailBox">
                <div className="label">Cobranza</div>
                <div className="value">{resumen.cobranza}%</div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {projectModal && (
        <div className="modalOverlay">
          <form className="modal modalSmall" onSubmit={crearProyecto}>
            <div className="modalHeader">
              <h2 className="modalTitle">Nuevo proyecto</h2>
              <button type="button" className="close" onClick={() => setProjectModal(false)}>
                <FiX />
              </button>
            </div>

            <div className="modalBody">
              <div className="formGrid">
                <div className="field full">
                  <label>Proyecto *</label>
                  <input
                    required
                    value={projectForm.nombre}
                    onChange={(e) =>
                      setProjectForm((p) => ({ ...p, nombre: e.target.value }))
                    }
                    placeholder="Nombre del proyecto"
                  />
                </div>

                <div className="field">
                  <label>Cliente</label>
                  <input
                    value={projectForm.cliente}
                    onChange={(e) =>
                      setProjectForm((p) => ({ ...p, cliente: e.target.value }))
                    }
                    placeholder="Cliente o área"
                  />
                </div>

                <div className="field">
                  <label>Responsable *</label>
                  <input
                    required
                    value={projectForm.responsable}
                    onChange={(e) =>
                      setProjectForm((p) => ({ ...p, responsable: e.target.value }))
                    }
                    placeholder="Responsable"
                  />
                </div>

                <div className="field">
                  <label>Fecha compromiso</label>
                  <input
                    type="date"
                    value={projectForm.fechaCompromiso}
                    onChange={(e) =>
                      setProjectForm((p) => ({
                        ...p,
                        fechaCompromiso: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="field">
                  <label>Prioridad</label>
                  <select
                    value={projectForm.prioridad}
                    onChange={(e) =>
                      setProjectForm((p) => ({ ...p, prioridad: e.target.value }))
                    }
                  >
                    <option value="ALTA">Alta</option>
                    <option value="MEDIA">Media</option>
                    <option value="BAJA">Baja</option>
                  </select>
                </div>

                <div className="field">
                  <label>Etapa inicial</label>
                  <select
                    value={projectForm.etapa}
                    onChange={(e) =>
                      setProjectForm((p) => ({ ...p, etapa: e.target.value }))
                    }
                  >
                    {ETAPAS.map((e) => (
                      <option key={e}>{e}</option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label>Monto contratado</label>
                  <input
                    type="number"
                    min="0"
                    value={projectForm.montoContratado}
                    onChange={(e) =>
                      setProjectForm((p) => ({
                        ...p,
                        montoContratado: e.target.value,
                      }))
                    }
                    placeholder="0"
                  />
                </div>

                <div className="field full">
                  <label>Siguiente acción</label>
                  <textarea
                    value={projectForm.proximoPaso}
                    onChange={(e) =>
                      setProjectForm((p) => ({ ...p, proximoPaso: e.target.value }))
                    }
                    placeholder="Primera acción para iniciar el seguimiento"
                  />
                </div>
              </div>

              <div
                style={{
                  marginTop: 14,
                  padding: 12,
                  borderRadius: 12,
                  background: "#f8fafc",
                  border: "1px solid #e5e7eb",
                  color: "#64748b",
                  fontSize: 12,
                  fontWeight: 750,
                  lineHeight: 1.45,
                }}
              >
                El avance, pagos, bloqueos, decisiones y cambios de estado se registran después desde la ficha del proyecto para generar línea de vida.
              </div>
            </div>

            <div className="modalActions">
              <button
                type="button"
                className="btnSecondary"
                onClick={() => setProjectModal(false)}
              >
                Cancelar
              </button>

              <button type="submit" className="btnPrimary">
                Crear proyecto
              </button>
            </div>
          </form>
        </div>
      )}
      {actionModal && (
        <div className="modalOverlay">
          <form className="modal modalSmall" onSubmit={guardarAccion}>
            <div className="modalHeader">
              <h2 className="modalTitle">
                {actionModal === "AVANCE" && "Registrar avance"}
                {actionModal === "PAGO" && "Registrar pago"}
                {actionModal === "ESTADO" && "Cambiar estado"}
                {actionModal === "BLOQUEO" && "Agregar bloqueo"}
                {actionModal === "RESOLVER_BLOQUEO" && "Resolver bloqueo"}
                {actionModal === "DECISION" && "Actualizar decisión"}
                {actionModal === "FECHA" && "Cambiar fecha"}
                {actionModal === "NOTA" && "Agregar nota"}
              </h2>
              <button type="button" className="close" onClick={() => setActionModal(null)}>
                <FiX />
              </button>
            </div>

            <div className="modalBody">
              <div className="formGrid">
                {actionModal === "AVANCE" && (
                  <>
                    <div className="field">
                      <label>Avance %</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={actionForm.avance}
                        onChange={(e) => setActionForm((p) => ({ ...p, avance: e.target.value }))}
                      />
                    </div>

                    <div className="field full">
                      <label>Siguiente acción</label>
                      <textarea
                        value={actionForm.proximoPaso}
                        onChange={(e) => setActionForm((p) => ({ ...p, proximoPaso: e.target.value }))}
                      />
                    </div>

                    <div className="field full">
                      <label>Comentario</label>
                      <textarea
                        value={actionForm.comentario}
                        onChange={(e) => setActionForm((p) => ({ ...p, comentario: e.target.value }))}
                      />
                    </div>
                  </>
                )}

                {actionModal === "PAGO" && (
                  <>
                    <div className="field">
                      <label>Monto pagado</label>
                      <input
                        type="number"
                        min="0"
                        value={actionForm.monto}
                        onChange={(e) => setActionForm((p) => ({ ...p, monto: e.target.value }))}
                      />
                    </div>

                    <div className="field">
                      <label>Fecha de pago</label>
                      <input
                        type="date"
                        value={actionForm.fechaPago}
                        onChange={(e) => setActionForm((p) => ({ ...p, fechaPago: e.target.value }))}
                      />
                    </div>

                    <div className="field">
                      <label>Próximo pago</label>
                      <input
                        type="date"
                        value={actionForm.fechaProximoPago}
                        onChange={(e) => setActionForm((p) => ({ ...p, fechaProximoPago: e.target.value }))}
                      />
                    </div>

                    <div className="field full">
                      <label>Nota de pago</label>
                      <textarea
                        value={actionForm.notaPago}
                        onChange={(e) => setActionForm((p) => ({ ...p, notaPago: e.target.value }))}
                      />
                    </div>
                  </>
                )}

                {actionModal === "ESTADO" && (
                  <>
                    <div className="field">
                      <label>Estado</label>
                      <select
                        value={actionForm.estado}
                        onChange={(e) => setActionForm((p) => ({ ...p, estado: e.target.value }))}
                      >
                        <option value="BIEN">Va bien</option>
                        <option value="ATENCION">Atención</option>
                        <option value="RIESGO">Riesgo</option>
                        <option value="BLOQUEADO">Bloqueado</option>
                      </select>
                    </div>

                    <div className="field">
                      <label>Etapa</label>
                      <select
                        value={actionForm.etapa}
                        onChange={(e) => setActionForm((p) => ({ ...p, etapa: e.target.value }))}
                      >
                        {ETAPAS.map((e) => (
                          <option key={e}>{e}</option>
                        ))}
                      </select>
                    </div>

                    <div className="field full">
                      <label>Comentario</label>
                      <textarea
                        value={actionForm.comentario}
                        onChange={(e) => setActionForm((p) => ({ ...p, comentario: e.target.value }))}
                      />
                    </div>
                  </>
                )}

                {actionModal === "BLOQUEO" && (
                  <>
                    <div className="field full">
                      <label>Bloqueo</label>
                      <textarea
                        value={actionForm.bloqueo}
                        onChange={(e) => setActionForm((p) => ({ ...p, bloqueo: e.target.value }))}
                      />
                    </div>

                    <div className="field full">
                      <label>Comentario</label>
                      <textarea
                        value={actionForm.comentario}
                        onChange={(e) => setActionForm((p) => ({ ...p, comentario: e.target.value }))}
                      />
                    </div>
                  </>
                )}

                {actionModal === "RESOLVER_BLOQUEO" && (
                  <div className="field full">
                    <label>Comentario de resolución</label>
                    <textarea
                      value={actionForm.comentario}
                      onChange={(e) => setActionForm((p) => ({ ...p, comentario: e.target.value }))}
                    />
                  </div>
                )}

                {actionModal === "DECISION" && (
                  <>
                    <div className="field full">
                      <label>Decisión requerida</label>
                      <textarea
                        value={actionForm.decision}
                        onChange={(e) => setActionForm((p) => ({ ...p, decision: e.target.value }))}
                      />
                    </div>

                    <div className="field full">
                      <label>Comentario</label>
                      <textarea
                        value={actionForm.comentario}
                        onChange={(e) => setActionForm((p) => ({ ...p, comentario: e.target.value }))}
                      />
                    </div>
                  </>
                )}

                {actionModal === "FECHA" && (
                  <>
                    <div className="field">
                      <label>Fecha compromiso</label>
                      <input
                        type="date"
                        value={actionForm.fechaCompromiso}
                        onChange={(e) => setActionForm((p) => ({ ...p, fechaCompromiso: e.target.value }))}
                      />
                    </div>

                    <div className="field full">
                      <label>Comentario</label>
                      <textarea
                        value={actionForm.comentario}
                        onChange={(e) => setActionForm((p) => ({ ...p, comentario: e.target.value }))}
                      />
                    </div>
                  </>
                )}

                {actionModal === "NOTA" && (
                  <div className="field full">
                    <label>Nota</label>
                    <textarea
                      value={actionForm.nota}
                      onChange={(e) => setActionForm((p) => ({ ...p, nota: e.target.value }))}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="modalActions">
              <button type="button" className="btnSecondary" onClick={() => setActionModal(null)}>
                Cancelar
              </button>

              <button type="submit" className="btnPrimary">
                Guardar
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}













